import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import { useExperimentsFilterOptions } from './hooks/useFilterOptions';
import {
    IExperiment,
    ExperimentsListResponse,
} from './types/experiment';
import {
    ExperimentCard,
    ExperimentsList,
    ExperimentFilters
} from './components/experiments';
import './ExperimentsView.css';

const ExperimentsView: React.FC = () => {
    const { token, user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL state
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const filterType = searchParams.get('type') || '';
    const filterStatus = searchParams.get('status') || '';
    const filterProvider = searchParams.get('provider') || '';
    const filterTag = searchParams.get('tag') || '';
    const viewMode = (searchParams.get('view') as 'grid' | 'list') || 'grid';
    const searchQuery = searchParams.get('search') || '';

    // Local state
    const [experiments, setExperiments] = useState<IExperiment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalExperiments, setTotalExperiments] = useState(0);

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Use optimized filter options hook with caching and preloading
    const { filterOptions: filterOptionsData } = useExperimentsFilterOptions();
    const availableTags = filterOptionsData.tags || [];

    // Update URL params helper
    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const fetchExperiments = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '20'
            });
            if (filterProvider) params.append('provider', filterProvider);
            if (filterType) params.append('type', filterType);
            if (filterStatus) params.append('status', filterStatus);
            if (filterTag) params.append('tags', filterTag);
            if (searchQuery) params.append('search', searchQuery);

            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/experiments?${params}`, {
                headers
            });

            if (!response.ok) {
                const errText = await response.text();
                throw new Error('Failed to fetch experiments: ' + (response.statusText || errText || 'Unknown error'));
            }

            const data: ExperimentsListResponse = await response.json();
            setExperiments(data.experiments as IExperiment[]);
            setTotalPages(data.pagination.totalPages);
            setTotalExperiments(data.pagination.total);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filterProvider, filterType, filterStatus, filterTag, searchQuery, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        fetchExperiments();
    }, [fetchExperiments]);

    const handlePageChange = (page: number) => {
        updateSearchParams({ page: page.toString() });
    };

    const handleTypeChange = (type: string) => {
        updateSearchParams({ type: type || null, page: '1' });
    };

    const handleStatusChange = (status: string) => {
        updateSearchParams({ status: status || null, page: '1' });
    };

    const handleProviderChange = (provider: string) => {
        updateSearchParams({ provider: provider || null, page: '1' });
    };

    const handleTagChange = (tag: string) => {
        updateSearchParams({ tag: tag || null, page: '1' });
    };

    const handleViewModeChange = (mode: 'grid' | 'list') => {
        updateSearchParams({ view: mode });
    };

    const handleClearFilters = () => {
        updateSearchParams({
            type: null,
            status: null,
            provider: null,
            tag: null,
            search: null,
            page: '1'
        });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateSearchParams({ search: value || null, page: '1' });
    };

    const handleCreateNew = () => {
        navigate('/experiments/new');
    };

    const hasActiveFilters = filterType || filterStatus || filterProvider || filterTag || searchQuery;

    if (loading && experiments.length === 0) {
        return (
            <div className="experiments-view">
                <div className="loading-container">
                    <div className="experiments-loader-flex">
                        <span className="material-icons loader-icon rotating">sync</span>
                        <span className="loader-text">{t('loading.experiments')}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="experiments-view">
            {/* Header */}
            <div className="experiments-header">
                <div className="header-title-section">
                    <h1>{t('experiments.title')}</h1>
                    <span className="experiments-count">
                        {totalExperiments} {t('experiments.experimentsCount')}
                    </span>
                </div>
                <div className="header-actions">
                    {user?.role === 'ADMIN' && (
                        <button className="btn-primary" onClick={handleCreateNew}>
                            <span className="material-icons">add</span>
                            {t('experiments.createNew')}
                        </button>
                    )}
                </div>
            </div>

            {/* Search and Controls Bar */}
            <div className="experiments-controls">
                <div className="search-section">
                    <div className="search-input-wrapper">
                        <span className="material-icons search-icon">search</span>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={t('experiments.searchPlaceholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button
                                className="search-clear-btn"
                                onClick={() => updateSearchParams({ search: null, page: '1' })}
                            >
                                <span className="material-icons">close</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="view-toggle">
                    <button
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('grid')}
                        title={t('experiments.viewMode.grid')}
                    >
                        <span className="material-icons">grid_view</span>
                    </button>
                    <button
                        className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('list')}
                        title={t('experiments.viewMode.list')}
                    >
                        <span className="material-icons">view_list</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <ExperimentFilters
                selectedType={filterType}
                selectedStatus={filterStatus}
                selectedProvider={filterProvider}
                selectedTag={filterTag}
                availableTags={availableTags}
                onTypeChange={handleTypeChange}
                onStatusChange={handleStatusChange}
                onProviderChange={handleProviderChange}
                onTagChange={handleTagChange}
                onClearFilters={handleClearFilters}
            />

            {/* Error State */}
            {error && (
                <div className="error-banner">
                    <span className="material-icons">error</span>
                    <span>{error}</span>
                    <button onClick={fetchExperiments} className="retry-btn">
                        <span className="material-icons">refresh</span>
                        {t('buttons.retry')}
                    </button>
                </div>
            )}

            {/* Results */}
            {experiments.length === 0 && !loading ? (
                <div className="empty-state">
                    <span className="material-icons">science</span>
                    <h3>
                        {hasActiveFilters
                            ? t('experiments.noDataFiltered')
                            : t('experiments.noData')}
                    </h3>
                    {hasActiveFilters && (
                        <button className="btn-secondary" onClick={handleClearFilters}>
                            {t('experiments.filters.clearAll')}
                        </button>
                    )}
                    {!hasActiveFilters && user?.role === 'ADMIN' && (
                        <button className="btn-primary" onClick={handleCreateNew}>
                            <span className="material-icons">add</span>
                            {t('experiments.createFirst')}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Grid View */}
                    {viewMode === 'grid' && (
                        <div className="experiments-grid">
                            {experiments.map((experiment) => (
                                <ExperimentCard key={experiment._id} experiment={experiment} />
                            ))}
                        </div>
                    )}

                    {/* List View */}
                    {viewMode === 'list' && (
                        <ExperimentsList experiments={experiments} loading={loading} />
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                disabled={currentPage === 1 || loading}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <span className="material-icons">chevron_left</span>
                                {t('pagination.previous')}
                            </button>

                            <div className="pagination-pages">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                className="pagination-btn"
                                disabled={currentPage === totalPages || loading}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                {t('pagination.next')}
                                <span className="material-icons">chevron_right</span>
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Loading overlay for subsequent fetches */}
            {loading && experiments.length > 0 && (
                <div className="loading-overlay">
                    <span className="material-icons rotating">sync</span>
                </div>
            )}
        </div>
    );
};

export default ExperimentsView;
